import { MODULES, getNavGroup } from '@solar/shared';
import { CornerDownLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/hooks/use-permissions';
import { resolveIcon } from '@/lib/icons';
import { cn } from '@/lib/utils';

/**
 * Command palette (Ctrl/Cmd + K).
 *
 * Today it jumps between modules, which is the highest-value part in an app with
 * 35 of them. Record search is a natural extension: the API needs one cross-module
 * search endpoint, and this component gains a second result group.
 */
export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);

  const navigate = useNavigate();
  const { canAccessModule } = usePermissions();

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    const available = MODULES.filter((module) => canAccessModule(module.key));

    if (!term) return available.slice(0, 8);

    return available
      .filter(
        (module) =>
          module.label.toLowerCase().includes(term) ||
          module.description.toLowerCase().includes(term) ||
          module.singular.toLowerCase().includes(term),
      )
      .slice(0, 10);
  }, [query, canAccessModule]);

  // Keep the highlight in range as the result set shrinks.
  useEffect(() => setHighlighted(0), [query]);

  const go = (path: string) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-8 w-full max-w-xs items-center gap-2 rounded-md border border-input bg-muted/40 px-2.5 text-xs text-muted-foreground transition-colors hover:bg-accent/60 md:w-64"
      >
        <Search className="size-3.5" />
        <span className="flex-1 text-left">Search modules…</span>
        <kbd className="hidden rounded border border-border bg-card px-1 font-mono text-2xs md:inline">
          Ctrl K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md" hideClose className="top-[18%] translate-y-0 p-0">
          <DialogTitle className="sr-only">Search</DialogTitle>

          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search className="size-4 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              placeholder="Jump to a module…"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setHighlighted((current) => Math.min(current + 1, results.length - 1));
                }
                if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setHighlighted((current) => Math.max(current - 1, 0));
                }
                if (event.key === 'Enter') {
                  const target = results[highlighted];
                  if (target) go(target.path);
                }
              }}
              className="h-11 border-0 shadow-none focus-visible:ring-0"
            />
          </div>

          <div className="max-h-80 overflow-y-auto p-1.5">
            {results.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                Nothing matches “{query}”.
              </p>
            ) : (
              results.map((module, index) => {
                const Icon = resolveIcon(module.icon);
                return (
                  <button
                    key={module.key}
                    type="button"
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => go(module.path)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors',
                      index === highlighted ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
                    )}
                  >
                    <Icon className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-1.5">
                        <span className="truncate text-sm font-medium">{module.label}</span>
                        {/* Section, so "Dispatch" is not confused with "Dealers". */}
                        <span className="shrink-0 text-2xs text-muted-foreground/70">
                          {getNavGroup(module.group)?.label}
                        </span>
                      </span>
                      <span className="block truncate text-2xs text-muted-foreground">
                        {module.description}
                      </span>
                    </span>
                    {index === highlighted ? (
                      <CornerDownLeft className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
